/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { utils } from 'pixi.js-legacy';
import { ComponentManager } from '@appCanvasCommon/component-system/ComponentManager';
import {
	LEVEL_OF_DETAIL_TABLE,
	LevelOfDetail,
	LEVEL_OF_DETAIL_SIGNALS,
} from '@appPlanApplication/lod/LevelOfDetail';
import { PlanState } from 'src/app/services/plan-state.service';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
/**
 * An abstract class. This class is used as our foundation for all components within the Canvas View renderer area.
 * Most of the methods are stubbed out by default. May expand in future to add more utility/helper methods.
 */
export abstract class Component extends utils.EventEmitter implements ComponentInterface {
	public active = true;
	/**
	 * Sets this component to active or inactive. An inactive component will never be invoked.
	 */
	public visible = false;
	/**
	 * This always points to the ComponentManager which is responsible for this component.
	 * Typically, a component is always self-contained. However, there may be cases where a component needs access to another component.
	 * By accessing the common manager, a component can request another component from the same stack.
	 */
	public manager: ComponentManager;
	/**
	 * This always points to the DisplayObject or Object where the ComponentManager is attached to.
	 * This is useful when you are setting up listeners on a parent and need easy access to them later to provide clean-up
	 */
	public managerParent: any;
	/**
	 * This property is used to set the detail level of this component. If the current view level is less than this value,
	 * then this component will never run.
	 */
	public lodLevel = LEVEL_OF_DETAIL_TABLE.ALWAYS;
	public canvasAngularLand: CanvasAngularLand;
	constructor() {
		super();
		LevelOfDetail.emitter.on(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
	}
	/**
	 * @param {number} newLod
	 * @param {number} oldLod
	 */
	public lodChanged = (newLod: number, oldLod: number): boolean => {
		return (this.visible = this.lodLevel <= newLod && this.active);
	};
	/**
	 * Called after the Component has been constructed. At this point, Component.parent and Component.manager should be set and can be safely used.
	 * @param args - The arguments that are being passed into the setup.
	 */
	public setup(...args: any[]) {}
	/**
	 * Called if the parent component manager has the updateData loop enabled. This is usually called by an outside event.
	 * @param args
	 */
	public updateData(...args: any[]) {}
	/**
	 * Called during each renderer tick.
	 * @param args
	 */
	public update(...args: any[]) {}
	/**
	 * Helper method to always return the DisplayObject or Object where the ComponentManager is attached to.
	 */
	public getParent() {
		return this.managerParent;
	}
	/**
	 * Always returns the ComponentManager responsible for managing this component.
	 */
	public getManager() {
		return this.manager;
	}
	/**
	 * Called when the component manager has a new component added
	 */
	public componentAdded(...args: any[]) {}

	/**
	 * Called when the component manager has a component removed.
	 * @param args
	 */
	public componentRemoved(...args: any[]) {}
	/**
	 * Called when a draw event is dispatched.
	 * @param args
	 */
	public draw(...args: any[]) {}
	public destroy(options?) {
		this.canvasAngularLand = null;
		LevelOfDetail.emitter.off(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
	}
}
